import { useEffect, useState } from 'react';

export interface LightboxPhoto {
  id: string;
  url: string;
  caption?: string;
}

/**
 * Lightbox — fullscreen image viewer with prev/next + keyboard nav.
 *
 * Pure presentational: parent passes a list of photos + an `openIndex`.
 * onClose returns control to parent. Esc closes; arrow keys navigate.
 */
export function Lightbox({
  photos,
  openIndex,
  onClose,
}: {
  photos: LightboxPhoto[];
  openIndex: number | null;
  onClose: () => void;
}) {
  const [idx, setIdx] = useState(openIndex ?? 0);

  useEffect(() => {
    if (openIndex !== null) setIdx(openIndex);
  }, [openIndex]);

  useEffect(() => {
    if (openIndex === null) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowRight') setIdx((i) => Math.min(i + 1, photos.length - 1));
      else if (e.key === 'ArrowLeft') setIdx((i) => Math.max(i - 1, 0));
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [openIndex, photos.length, onClose]);

  if (openIndex === null || !photos[idx]) return null;
  const photo = photos[idx];
  const total = photos.length;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/90"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <button
        type="button"
        className="absolute top-4 right-4 text-white/80 hover:text-white text-3xl px-3 py-1 leading-none"
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        aria-label="Close"
      >
        ×
      </button>

      {idx > 0 && (
        <button
          type="button"
          className="absolute left-4 top-1/2 -translate-y-1/2 text-white/70 hover:text-white text-5xl px-4 py-2 leading-none"
          onClick={(e) => { e.stopPropagation(); setIdx(idx - 1); }}
          aria-label="Previous"
        >
          ‹
        </button>
      )}
      {idx < total - 1 && (
        <button
          type="button"
          className="absolute right-4 top-1/2 -translate-y-1/2 text-white/70 hover:text-white text-5xl px-4 py-2 leading-none"
          onClick={(e) => { e.stopPropagation(); setIdx(idx + 1); }}
          aria-label="Next"
        >
          ›
        </button>
      )}

      <figure
        className="max-w-[90vw] max-h-[85vh] flex flex-col items-center"
        onClick={(e) => e.stopPropagation()}
      >
        <img
          src={photo.url}
          alt={photo.caption || ''}
          className="max-w-full max-h-[80vh] object-contain rounded"
        />
        {(photo.caption || total > 1) && (
          <figcaption className="mt-3 text-white/80 text-sm text-center max-w-3xl">
            {photo.caption}
            {photo.caption && total > 1 && <span className="mx-2">·</span>}
            {total > 1 && (
              <span className="num text-white/60">
                {idx + 1} of {total}
              </span>
            )}
          </figcaption>
        )}
      </figure>
    </div>
  );
}
